import { useState } from "react";
import { toast } from "sonner";
import { Copy, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useStream } from "@/lib/stream";
import { copyText } from "@/lib/copy";
import { SendDraft } from "@/components/ai/SendDraft";

const Choice = ({ label, value, onChange, items, testId }) => (
  <div className="space-y-1.5"><Label>{label}</Label>
    <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
  </div>
);

export const DraftPanel = () => {
  const stream = useStream();
  const [f, setF] = useState({ kind: "email", tone: "formel", audience: "Praticiens inscrits", subject: "", details: "" });
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    setOut("");
    try { await stream("/ai/draft", f, setOut); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="ai-draft-panel">
      <form onSubmit={run} className="bg-white border border-stone-200 rounded-lg p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Choice label="Format" value={f.kind} onChange={set("kind")} items={[["email", "E-mail"], ["notification", "Notification"], ["sms", "SMS"]]} testId="ai-draft-kind" />
          <Choice label="Ton" value={f.tone} onChange={set("tone")} items={[["formel", "Formel"], ["chaleureux", "Chaleureux"], ["urgent", "Urgent"]]} testId="ai-draft-tone" />
        </div>
        <div className="space-y-1.5"><Label>Destinataires</Label><Input value={f.audience} onChange={set("audience")} data-testid="ai-draft-audience" /></div>
        <div className="space-y-1.5"><Label>Sujet *</Label><Input required minLength={3} value={f.subject} onChange={set("subject")} placeholder="Ex. Campagne de vaccination rougeole" data-testid="ai-draft-subject" /></div>
        <div className="space-y-1.5"><Label>Éléments à inclure</Label><Textarea rows={5} value={f.details} onChange={set("details")} placeholder="Dates, lieux, consignes… (évitez les données personnelles)" data-testid="ai-draft-details" /></div>
        <Button type="submit" disabled={busy} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid="ai-draft-run"><PenLine className="h-4 w-4 mr-1.5" /> {busy ? "Rédaction…" : "Rédiger"}</Button>
      </form>
      <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-3">
        <div className="flex items-center"><h3 className="font-heading text-base font-bold text-stone-900">Brouillon</h3>
          {out && !busy && <Button variant="outline" size="sm" className="ml-auto" onClick={() => copyText(out, "Brouillon copié")} data-testid="ai-draft-copy"><Copy className="h-4 w-4 mr-1.5" /> Copier</Button>}</div>
        <Textarea rows={16} value={out} onChange={(e) => setOut(e.target.value)} placeholder="Le brouillon apparaîtra ici, modifiable avant copie." data-testid="ai-draft-output" />
        <p className="text-xs text-stone-500">Les [crochets] signalent les informations à compléter. Relisez avant envoi.</p>
      </div>
      {f.kind === "email" && out && !busy && <div className="lg:col-span-2"><SendDraft draft={out} /></div>}
    </div>
  );
};
